import type { AssistantSurface } from '@luxuryescapes/contract-trip'
import {
  STREAM_EVENT,
  type ChatToolLoadingStateData,
  type ChatMetadataUpdatedData,
  type ContractMessagePart,
  type ItineraryUpdate,
  type EnrichmentCompleteEvent,
  type HotelSearchResultsData,
  type ExperienceSearchResultsData,
  type HotelSearchErrorData,
  type ExperienceSearchErrorData,
  type ChatUiActionData,
  type SetRightPanelViewActionData,
  type AssistantSurfaceContentActionSnapshot,
  type StatusEventData,
  type ProgressiveUiPartEvent,
} from 'tripPlanner/api/types'
import { isRecord } from 'lib/object/objectUtils'
import { isStreamedProgressiveUiData } from './streamedProgressiveUi'

const ASSISTANT_SURFACES: ReadonlySet<AssistantSurface> = new Set([
  'trip_planner', 'accommodation_offer_page', 'search_page',
] satisfies Array<AssistantSurface>)

function isAssistantSurface(value: unknown): value is AssistantSurface {
  return typeof value === 'string' && ASSISTANT_SURFACES.has(value as AssistantSurface)
}

// Shallow, discriminant-only guards: the backend contract nests full entity/UI payloads several
// levels deep (TripStopWithDays, TripChatItem, ui_component data variants, etc.) -- validating
// every field here would duplicate the contract package's own schema. These only confirm the
// stream sent an array of objects carrying a recognized discriminant, so a malformed/corrupted
// event is rejected instead of being cast straight through.
const ITINERARY_UPDATE_KINDS = new Set<ItineraryUpdate['kind']>([
  'stop_added', 'stop_updated', 'stop_deleted',
  'day_added', 'day_deleted',
  'item_added', 'item_updated', 'item_deleted',
])

function isItineraryUpdate(value: unknown): value is ItineraryUpdate {
  return isRecord(value) && typeof value.kind === 'string' && ITINERARY_UPDATE_KINDS.has(value.kind as ItineraryUpdate['kind'])
}

const CONTRACT_MESSAGE_PART_TYPES = new Set<ContractMessagePart['type']>([
  'text', 'ui_component', 'trip_plan_content',
])

function isContractMessagePart(value: unknown): value is ContractMessagePart {
  return isRecord(value) && typeof value.type === 'string' && CONTRACT_MESSAGE_PART_TYPES.has(value.type as ContractMessagePart['type'])
}

/**
 * StreamEvent represents all possible parsed events from the message stream.
 */
export type StreamEvent =
  | { event: typeof STREAM_EVENT.MESSAGE_START; messageId: string; role: 'ASSISTANT' | 'USER' | string }
  | { event: typeof STREAM_EVENT.PARTS_UPDATE; messageId: string; parts: Array<ContractMessagePart> }
  | { event: typeof STREAM_EVENT.MESSAGE_END; messageId: string; parts: Array<ContractMessagePart>; oaiConversationId?: string; rawContent?: string }
  | ProgressiveUiPartEvent
  | { event: typeof STREAM_EVENT.ITINERARY_UPDATE_STARTED }
  | { event: typeof STREAM_EVENT.ITINERARY_UPDATES; updates: Array<ItineraryUpdate> }
  | { event: typeof STREAM_EVENT.CONTENT_COMPLETE }
  | { event: typeof STREAM_EVENT.ENRICHMENT_COMPLETE; data: EnrichmentCompleteEvent }
  | { event: typeof STREAM_EVENT.CHAT_LOADING_STATE; data: ChatToolLoadingStateData }
  | { event: typeof STREAM_EVENT.CHAT_METADATA_UPDATED; data: ChatMetadataUpdatedData }
  | { event: typeof STREAM_EVENT.HOTEL_SEARCH_RESULTS; data: HotelSearchResultsData }
  | { event: typeof STREAM_EVENT.EXPERIENCE_SEARCH_RESULTS; data: ExperienceSearchResultsData }
  | { event: typeof STREAM_EVENT.HOTEL_SEARCH_ERROR; data: HotelSearchErrorData }
  | { event: typeof STREAM_EVENT.EXPERIENCE_SEARCH_ERROR; data: ExperienceSearchErrorData }
  | { event: typeof STREAM_EVENT.CHAT_UI_ACTION; data: ChatUiActionData }
  | AssistantSurfaceContentActionSnapshot
  | { event: typeof STREAM_EVENT.STATUS; data: StatusEventData }
  | { event: typeof STREAM_EVENT.HEARTBEAT }
  | { event: typeof STREAM_EVENT.TOKEN_BATCH; text: string }

/**
 * Type guard for EnrichmentCompleteEvent from parsed JSON objects.
 */
function isEnrichmentCompleteEvent(
  obj: Record<string, unknown>,
): obj is EnrichmentCompleteEvent {
  return obj.entityType === 'stop' || obj.entityType === 'item'
}

/**
 * Shared shape guard for hotel/experience search results payloads.
 */
function isSearchResultsDataShape(
  data: unknown,
): data is {
  searchQuery: string
  offers: Array<{ id: string; name: string }>
} {
  if (!isRecord(data)) return false
  return (
    typeof data.searchQuery === 'string' &&
    Array.isArray(data.offers) &&
    data.offers.every((offer: unknown) => {
      if (!isRecord(offer)) return false
      return typeof offer.id === 'string' && typeof offer.name === 'string'
    })
  )
}

/**
 * Type guard for HotelSearchResultsData from parsed JSON objects.
 */
function isHotelSearchResultsData(
  data: unknown,
): data is HotelSearchResultsData {
  return isSearchResultsDataShape(data)
}

function isExperienceSearchResultsData(
  data: unknown,
): data is ExperienceSearchResultsData {
  return isSearchResultsDataShape(data)
}

/**
 * Shared shape guard for hotel/experience search error payloads.
 */
function isSearchErrorDataShape(
  data: unknown,
): data is { message: string } {
  if (!isRecord(data)) return false
  return typeof data.message === 'string'
}

/**
 * Type guard for HotelSearchErrorData from parsed JSON objects.
 */
function isHotelSearchErrorData(
  data: unknown,
): data is HotelSearchErrorData {
  return isSearchErrorDataShape(data)
}

function isExperienceSearchErrorData(
  data: unknown,
): data is ExperienceSearchErrorData {
  return isSearchErrorDataShape(data)
}

function isChatToolLoadingStateData(
  data: unknown,
): data is ChatToolLoadingStateData {
  if (!isRecord(data)) return false

  return (
    data.kind === 'tool_call' &&
    (data.phase === 'start' || data.phase === 'complete') &&
    typeof data.key === 'string' &&
    typeof data.callId === 'string'
  )
}

function isChatMetadataUpdatedData(
  data: unknown,
): data is ChatMetadataUpdatedData {
  if (!isRecord(data)) return false

  return (
    typeof data.chatId === 'number' &&
    Number.isFinite(data.chatId) &&
    typeof data.name === 'string' &&
    typeof data.updatedAt === 'string'
  )
}

function isStatusEventData(obj: Record<string, unknown>): obj is StatusEventData {
  const { phase } = obj
  if (phase === 'reasoning') return true
  if (phase === 'tool_calling' && typeof obj.tool === 'string') return true
  return false
}

function isSetRightPanelViewName(view: unknown): view is SetRightPanelViewActionData['view'] {
  return view === 'map' || view === 'timeline' || view === 'list'
}

function isSetRightPanelViewActionData(data: Record<string, unknown>): boolean {
  if (!(data.action === 'set_right_panel_view' && isSetRightPanelViewName(data.view))) {
    return false
  }
  const hasLat = Object.prototype.hasOwnProperty.call(data, 'lat')
  const hasLng = Object.prototype.hasOwnProperty.call(data, 'lng')
  const hasZoom = Object.prototype.hasOwnProperty.call(data, 'zoom')
  if (hasLat !== hasLng || hasLat !== hasZoom) return false
  if (!hasLat) return true
  return typeof data.lat === 'number' && Number.isFinite(data.lat) &&
    typeof data.lng === 'number' && Number.isFinite(data.lng) &&
    typeof data.zoom === 'number' && Number.isFinite(data.zoom)
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string'
}

function isFocusSurfaceContentActionData(data: Record<string, unknown>): boolean {
  return data.action === 'focus_surface_content' &&
    typeof data.messageId === 'string' &&
    typeof data.snapshotRefId === 'string' &&
    isAssistantSurface(data.surface) &&
    typeof data.surfaceContextScopeKey === 'string' &&
    typeof data.snapshotFingerprint === 'string' &&
    typeof data.targetId === 'string' &&
    typeof data.highlight === 'boolean' &&
    isOptionalString(data.fallbackTargetId)
}

function isChatUiActionData(data: unknown): data is ChatUiActionData {
  if (!isRecord(data)) return false
  if (data.action === 'set_right_panel_view') return isSetRightPanelViewActionData(data)
  if (data.action === 'focus_surface_content') return isFocusSurfaceContentActionData(data)
  return false
}

function isContentActionSnapshotEvent(value: unknown): value is AssistantSurfaceContentActionSnapshot {
  if (!isRecord(value)) return false
  return typeof value.messageId === 'string' &&
    typeof value.snapshotRefId === 'string' &&
    isAssistantSurface(value.surface) &&
    typeof value.surfaceContextScopeKey === 'string' &&
    typeof value.snapshotFingerprint === 'string' &&
    Array.isArray(value.autoFocusableTargets) &&
    value.autoFocusableTargets.every((entry) =>
      isRecord(entry) &&
      typeof entry.targetId === 'string' &&
      isOptionalString(entry.fallbackTargetId),
    )
}

function isUiPartPatchShape(obj: Record<string, unknown>): obj is {
  messageId: string
  partId: string
  op: 'set' | 'append'
  path: Array<string>
  value: unknown
} {
  return typeof obj.messageId === 'string' &&
    typeof obj.partId === 'string' &&
    (obj.op === 'set' || obj.op === 'append') &&
    Array.isArray(obj.path) &&
    obj.path.every((segment) => typeof segment === 'string')
}

/**
 * Parse a single line of JSONL from the message stream into a typed StreamEvent.
 * Returns null if the line is not valid JSON or not a recognized event type.
 */
export function parseStreamEvent(line: string): StreamEvent | null {
  let parsed: unknown

  try {
    parsed = JSON.parse(line)
  } catch {
    return null
  }

  if (!isRecord(parsed)) return null

  const { event, messageId, role, parts, oaiConversationId, rawContent } = parsed

  if (event === STREAM_EVENT.ITINERARY_UPDATE_STARTED) {
    return { event: STREAM_EVENT.ITINERARY_UPDATE_STARTED }
  }

  if (event === STREAM_EVENT.ITINERARY_UPDATES) {
    const { updates } = parsed
    if (Array.isArray(updates) && updates.every(isItineraryUpdate)) {
      return {
        event: STREAM_EVENT.ITINERARY_UPDATES,
        updates,
      }
    }
  }

  if (event === STREAM_EVENT.CONTENT_COMPLETE) {
    return { event: STREAM_EVENT.CONTENT_COMPLETE }
  }

  // Handle enrichment_complete events (emitted as the entity itself, not wrapped)
  if (event === STREAM_EVENT.ENRICHMENT_COMPLETE) {
    if (isEnrichmentCompleteEvent(parsed)) {
      return {
        event: STREAM_EVENT.ENRICHMENT_COMPLETE,
        data: parsed,
      }
    }
  }

  if (event === STREAM_EVENT.CHAT_LOADING_STATE) {
    const data = parsed.data
    if (isChatToolLoadingStateData(data)) {
      return {
        event: STREAM_EVENT.CHAT_LOADING_STATE,
        data,
      }
    }
  }

  if (event === STREAM_EVENT.CHAT_METADATA_UPDATED) {
    const data = parsed.data
    if (isChatMetadataUpdatedData(data)) {
      return {
        event: STREAM_EVENT.CHAT_METADATA_UPDATED,
        data,
      }
    }
  }

  // Handle hotel_search_results events
  if (event === STREAM_EVENT.HOTEL_SEARCH_RESULTS) {
    const data = parsed.data
    if (isHotelSearchResultsData(data)) {
      return {
        event: STREAM_EVENT.HOTEL_SEARCH_RESULTS,
        data,
      }
    }
  }

  // Handle hotel_search_error events
  if (event === STREAM_EVENT.HOTEL_SEARCH_ERROR) {
    const data = parsed.data
    if (isHotelSearchErrorData(data)) {
      return {
        event: STREAM_EVENT.HOTEL_SEARCH_ERROR,
        data,
      }
    }
  }

  if (event === STREAM_EVENT.EXPERIENCE_SEARCH_ERROR) {
    const data = parsed.data
    if (isExperienceSearchErrorData(data)) {
      return {
        event: STREAM_EVENT.EXPERIENCE_SEARCH_ERROR,
        data,
      }
    }
  }

  if (event === STREAM_EVENT.EXPERIENCE_SEARCH_RESULTS) {
    const data = parsed.data
    if (isExperienceSearchResultsData(data)) {
      return {
        event: STREAM_EVENT.EXPERIENCE_SEARCH_RESULTS,
        data,
      }
    }
  }

  if (event === STREAM_EVENT.CHAT_UI_ACTION) {
    const data = parsed.data
    if (isChatUiActionData(data)) {
      return { event: STREAM_EVENT.CHAT_UI_ACTION, data }
    }
  }

  if (event === STREAM_EVENT.ASSISTANT_SURFACE_CONTENT_ACTION_SNAPSHOT) {
    if (isContentActionSnapshotEvent(parsed)) {
      return parsed
    }
  }

  if (event === STREAM_EVENT.STATUS) {
    if (isStatusEventData(parsed)) {
      if (parsed.phase === 'reasoning') {
        const summaries = Array.isArray(parsed.summaries)
          ? parsed.summaries.filter((s): s is string => typeof s === 'string')
          : undefined
        return { event: STREAM_EVENT.STATUS, data: { phase: 'reasoning', ...(summaries && summaries.length > 0 && { summaries }) } }
      }
      const args = isRecord(parsed.args)
        ? parsed.args
        : {}
      return { event: STREAM_EVENT.STATUS, data: { phase: 'tool_calling', tool: parsed.tool, args } }
    }
  }

  if (event === STREAM_EVENT.HEARTBEAT) {
    return { event: STREAM_EVENT.HEARTBEAT }
  }

  if (event === STREAM_EVENT.TOKEN_BATCH && typeof parsed.text === 'string') {
    return { event: STREAM_EVENT.TOKEN_BATCH, text: parsed.text }
  }

  if (
    (event === STREAM_EVENT.UI_PART_STARTED || event === STREAM_EVENT.UI_PART_COMPLETED) &&
    typeof messageId === 'string' &&
    typeof parsed.partId === 'string' &&
    isStreamedProgressiveUiData(parsed.data)
  ) {
    return {
      event,
      messageId,
      partId: parsed.partId,
      data: parsed.data,
    }
  }

  if (event === STREAM_EVENT.UI_PART_PATCH && isUiPartPatchShape(parsed)) {
    return {
      event: STREAM_EVENT.UI_PART_PATCH,
      messageId: parsed.messageId,
      partId: parsed.partId,
      op: parsed.op,
      path: parsed.path,
      value: parsed.value,
    }
  }

  if (
    event === STREAM_EVENT.UI_PART_FAILED &&
    typeof messageId === 'string' &&
    typeof parsed.partId === 'string'
  ) {
    return {
      event: STREAM_EVENT.UI_PART_FAILED,
      messageId,
      partId: parsed.partId,
    }
  }

  if (typeof messageId !== 'string') return null

  if (event === STREAM_EVENT.MESSAGE_START) {
    return {
      event: STREAM_EVENT.MESSAGE_START,
      messageId,
      role: typeof role === 'string' ? role : 'ASSISTANT',
    }
  }

  if ((event === STREAM_EVENT.PARTS_UPDATE || event === STREAM_EVENT.MESSAGE_END) && Array.isArray(parts) && parts.every(isContractMessagePart)) {
    const baseEvent = { messageId, parts }

    if (event === STREAM_EVENT.PARTS_UPDATE) {
      return { event: STREAM_EVENT.PARTS_UPDATE, ...baseEvent }
    }

    return {
      event: STREAM_EVENT.MESSAGE_END,
      ...baseEvent,
      ...(typeof oaiConversationId === 'string' ? { oaiConversationId } : {}),
      ...(typeof rawContent === 'string' ? { rawContent } : {}),
    }
  }

  return null
}
